import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("hafani_admin_token")}`,
});

const REQUIREMENT_LABELS = {
  "individual.dob": "Date de naissance du président",
  "individual.id_number": "Numéro d'identification du président",
  "individual.address": "Adresse du président",
  "individual.phone": "Téléphone du président",
  "individual.verification": "Pièce d'identité du président",
  "external_account": "IBAN de l'association",
  "business_profile.url": "Site web de l'association",
  "company.tax_id": "Numéro RNA ou SIREN",
  "tos_acceptance": "Acceptation des conditions générales Stripe",
};

const formatRequirements = (requirements = []) => {
  const labels = new Set();
  for (const req of requirements) {
    let matched = false;
    for (const [prefix, label] of Object.entries(REQUIREMENT_LABELS)) {
      if (req.startsWith(prefix)) {
        labels.add(label);
        matched = true;
        break;
      }
    }
    if (!matched) labels.add(req);
  }
  return [...labels];
};

const css = `
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .step-enter { animation: fadeSlideUp 0.4s cubic-bezier(0.22,1,0.36,1) both; }
  .step-enter-1 { animation-delay: 0.05s; }
  .step-enter-2 { animation-delay: 0.12s; }
  .step-enter-3 { animation-delay: 0.20s; }
  .step-enter-4 { animation-delay: 0.28s; }
  .step-enter-5 { animation-delay: 0.36s; }
`;

/* ─── Progress indicator ─── */
const StepIndicator = ({ current }) => {
  const steps = ["Préparation", "Confirmation", "Activation"];
  const indices = { checklist: 0, prefill: 1, return: 2 };
  const active = indices[current] ?? 0;

  return (
    <div className="flex items-center justify-center gap-0 mb-10">
      {steps.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
              style={{
                backgroundColor: i < active ? "#059669" : i === active ? "#111827" : "#D1D5DB",
                color: i <= active ? "#fff" : "#6B7280",
              }}
            >
              {i < active ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : i + 1}
            </div>
            <span
              className="mt-1.5 text-xs font-medium hidden sm:block"
              style={{ color: i === active ? "#111827" : "#9CA3AF" }}
            >
              {label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className="w-16 sm:w-24 h-px mx-1 transition-all duration-500"
              style={{ backgroundColor: i < active ? "#059669" : "#D1D5DB" }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

/* ─── Checklist Item ─── */
const CheckItem = ({ title, description, helpText, helpHref, delay = 1 }) => (
  <div className={`step-enter step-enter-${delay} flex gap-4 py-4 border-b border-slate-100 last:border-0`}>
    <div className="flex-shrink-0 mt-0.5">
      <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "#ECFDF5" }}>
        <svg className="w-3.5 h-3.5" style={{ color: "#059669" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{description}</p>
      {helpText && (
        <a
          href={helpHref || "#"}
          onClick={e => !helpHref && e.preventDefault()}
          className="inline-flex items-center gap-1 text-xs mt-1 font-medium transition-colors"
          style={{ color: "#059669" }}
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {helpText}
        </a>
      )}
    </div>
  </div>
);

/* ─── Info row for Screen 2 ─── */
const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-2.5 border-b border-slate-50 last:border-0">
    <span className="text-xs text-slate-400 font-medium uppercase tracking-wide flex-shrink-0 w-40">{label}</span>
    <span className="text-sm text-slate-800 text-right">{value || <span className="text-slate-400 italic">Non renseigné</span>}</span>
  </div>
);

/* ─── Status row for Screen 3 ─── */
const StatusRow = ({ label, state }) => {
  const configs = {
    pending:  { icon: "⏳", color: "#D97706", bg: "#FFFBEB", text: "En attente" },
    checking: { icon: "⏳", color: "#D97706", bg: "#FFFBEB", text: "Vérification" },
    active:   { icon: "✅", color: "#059669", bg: "#ECFDF5", text: "Actif" },
  };
  const cfg = configs[state] || configs.pending;
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
      <span className="text-sm text-slate-700">{label}</span>
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
        style={{ color: cfg.color, backgroundColor: cfg.bg }}
      >
        <span>{cfg.icon}</span>
        {cfg.text}
      </span>
    </div>
  );
};

/* ─── Screen 1 — Checklist ─── */
const ChecklistStep = ({ onContinue, onSkip }) => (
  <div>
    <div className="step-enter step-enter-1 rounded-2xl overflow-hidden mb-6" style={{ background: "linear-gradient(135deg, #111827 0%, #1F2937 100%)" }}>
      <div className="p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#635BFF" }}>
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl text-white leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
              Activez les paiements en ligne avec Stripe
            </h1>
          </div>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: "#9CA3AF" }}>
          Encaissez les cotisations de vos adhérents par carte bancaire ou prélèvement SEPA.
          Délai d'activation : <span style={{ color: "#D1D5DB", fontWeight: 500 }}>10 à 15 minutes</span>.
        </p>
      </div>
    </div>

    <div className="step-enter step-enter-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 mb-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">
        Avant de commencer, assurez-vous d'avoir
      </p>
      <CheckItem
        delay={2}
        title="Votre numéro RNA (W + 9 chiffres)"
        description="Visible sur votre récépissé de déclaration en préfecture ou dans le JOAFE."
        helpText="Je ne retrouve pas mon numéro RNA"
      />
      <CheckItem
        delay={3}
        title="Un IBAN au nom de votre association"
        description="Le compte doit être au nom de l'association, pas du président à titre personnel."
        helpText="Pourquoi cette exigence ?"
      />
      <CheckItem
        delay={4}
        title="La pièce d'identité du président"
        description="Carte nationale d'identité, passeport ou titre de séjour (recto/verso, image ou PDF)."
      />
      <CheckItem
        delay={5}
        title="Les coordonnées personnelles du président"
        description="Date de naissance, adresse personnelle, numéro de téléphone."
      />
    </div>

    <div className="step-enter step-enter-3 flex items-start gap-3 rounded-xl px-4 py-3.5 mb-6"
         style={{ backgroundColor: "#F8F7F5", border: "1px solid #E5E2DC" }}>
      <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#D97706" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <p className="text-sm text-slate-600 leading-relaxed">
        <span className="font-semibold text-slate-700">Bon à savoir — </span>
        Stripe peut demander des pièces supplémentaires (statuts, PV d'AG) si vous dépassez un certain volume de transactions. Vous pourrez les fournir ultérieurement.
      </p>
    </div>

    <div className="step-enter step-enter-4 flex flex-col sm:flex-row gap-3">
      <button
        onClick={onContinue}
        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
        style={{ backgroundColor: "#635BFF" }}
      >
        Continuer vers Stripe
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </button>
      <button
        onClick={onSkip}
        className="flex-1 sm:flex-none px-5 py-3.5 rounded-xl text-sm font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 transition-all"
      >
        Plus tard — garder PayPal
      </button>
    </div>
  </div>
);

/* ─── Screen 2 — Pre-fill recap ─── */
const PrefillStep = ({ association, adminEmail, onConfirm, onBack, connecting }) => {
  const [accepted, setAccepted] = useState(false);

  return (
    <div>
      <div className="step-enter step-enter-1 mb-6">
        <h2 className="text-2xl sm:text-3xl text-slate-900 mb-2"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
          Nous transmettons à Stripe ces informations
        </h2>
        <p className="text-sm text-slate-500">pour vous éviter une nouvelle saisie sur leur formulaire.</p>
      </div>

      <div className="step-enter step-enter-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5 mb-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Informations sur votre association</p>
        <InfoRow label="Nom légal" value={association?.name} />
        <InfoRow label="Ville" value={association?.city} />
        <InfoRow label="Email de contact" value={association?.contact_email} />
        <InfoRow label="Activité" value="Association loi 1901" />
        <button className="mt-3 text-xs font-medium transition-colors" style={{ color: "#059669" }}>
          Modifier ces informations dans mon profil
        </button>
      </div>

      <div className="step-enter step-enter-3 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5 mb-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Représentant légal</p>
        <InfoRow label="Email" value={adminEmail} />
        <InfoRow label="Nom" value={null} />
        <InfoRow label="Fonction" value={null} />
        <button className="mt-3 text-xs font-medium transition-colors" style={{ color: "#059669" }}>
          Modifier ces informations dans mon profil
        </button>
      </div>

      <div className="step-enter step-enter-4 rounded-xl px-5 py-4 mb-5" style={{ backgroundColor: "#F8F7F5", border: "1px solid #E5E2DC" }}>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Ce qui reste à compléter sur Stripe</p>
        <ul className="space-y-2">
          {[
            "Date et lieu de naissance du président",
            "Pièce d'identité du président",
            "IBAN de l'association",
          ].map(item => (
            <li key={item} className="flex items-center gap-2 text-sm text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#9CA3AF" }} />
              {item}
            </li>
          ))}
        </ul>
      </div>

      <div className="step-enter step-enter-5 mb-6">
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative mt-0.5 flex-shrink-0">
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="sr-only"
            />
            <div
              className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all"
              style={{
                borderColor: accepted ? "#635BFF" : "#D1D5DB",
                backgroundColor: accepted ? "#635BFF" : "#fff",
              }}
            >
              {accepted && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <span className="text-sm text-slate-600 leading-relaxed">
            J'accepte les conditions générales de Stripe et celles de{" "}
            <span className="font-semibold text-slate-800">Hafani MS</span>{" "}
            relatives à l'encaissement de cotisations.{" "}
            <button className="underline underline-offset-2 font-medium transition-colors" style={{ color: "#059669" }}>
              Lire les conditions Hafani MS
            </button>
          </span>
        </label>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <button
          onClick={onConfirm}
          disabled={!accepted || connecting}
          className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
          style={{ backgroundColor: "#635BFF" }}
        >
          {connecting ? (
            <>
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Redirection vers Stripe…
            </>
          ) : (
            <>
              Continuer sur Stripe
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </>
          )}
        </button>
        <button
          onClick={onBack}
          className="flex-1 sm:flex-none px-5 py-3.5 rounded-xl text-sm font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 transition-all"
        >
          ← Annuler
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-400">
        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Vos données sont chiffrées et transmises à Stripe, prestataire de paiement agréé en Europe.
      </div>
    </div>
  );
};

/* ─── Screen 3 — Return variants ─── */
const ReturnStep = ({ stripeStatus, onResume, onDashboard, connecting }) => {
  if (!stripeStatus) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }

  const { stripe_charges_enabled, stripe_payouts_enabled, requirements_currently_due = [] } = stripeStatus;

  if (stripe_charges_enabled && stripe_payouts_enabled) {
    return (
      <div className="step-enter step-enter-1">
        <div className="rounded-2xl overflow-hidden mb-6" style={{ background: "linear-gradient(135deg, #064E3B 0%, #065F46 100%)" }}>
          <div className="p-6 sm:p-8 flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(255,255,255,0.15)" }}>
              <span className="text-2xl">🎉</span>
            </div>
            <div>
              <h2 className="text-2xl text-white mb-1.5"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
                Vos paiements en ligne sont activés !
              </h2>
              <p className="text-sm" style={{ color: "#A7F3D0" }}>
                Vos adhérents peuvent désormais payer par carte bancaire ou prélèvement SEPA.
              </p>
            </div>
          </div>
        </div>

        <div className="step-enter step-enter-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5 mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">État de l'activation</p>
          <StatusRow label="Paiements par carte" state="active" />
          <StatusRow label="Prélèvement SEPA" state="active" />
          <StatusRow label="Versements vers votre IBAN" state="active" />
        </div>

        <div className="step-enter step-enter-3 rounded-xl px-5 py-4 mb-6" style={{ backgroundColor: "#F8F7F5", border: "1px solid #E5E2DC" }}>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Prochaines étapes recommandées</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "#059669" }} />
              Configurez le moyen de paiement par défaut sur vos formulaires d'adhésion.
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-600">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: "#059669" }} />
              Vérifiez vos modèles de reçu et d'attestation fiscale.
            </li>
          </ul>
        </div>

        <button
          onClick={onDashboard}
          className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
          style={{ backgroundColor: "#111827" }}
        >
          Retour au tableau de bord
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    );
  }

  if (requirements_currently_due.length > 0) {
    const labels = formatRequirements(requirements_currently_due);
    return (
      <div className="step-enter step-enter-1">
        <div className="rounded-2xl overflow-hidden mb-6" style={{ background: "linear-gradient(135deg, #78350F 0%, #92400E 100%)" }}>
          <div className="p-6 sm:p-8 flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(255,255,255,0.15)" }}>
              <span className="text-2xl">⚠️</span>
            </div>
            <div>
              <h2 className="text-2xl text-white mb-1.5"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
                Stripe a besoin d'informations complémentaires
              </h2>
              <p className="text-sm" style={{ color: "#FDE68A" }}>
                Pour finaliser l'activation, quelques éléments restent à fournir.
              </p>
            </div>
          </div>
        </div>

        <div className="step-enter step-enter-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5 mb-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Éléments requis</p>
          <ul className="space-y-2.5">
            {labels.map(label => (
              <li key={label} className="flex items-center gap-3 text-sm text-slate-700">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#FEF3C7" }}>
                  <svg className="w-3 h-3" style={{ color: "#D97706" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01" />
                  </svg>
                </div>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="step-enter step-enter-3 flex items-start gap-3 rounded-xl px-4 py-3 mb-6"
             style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }}>
          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#059669" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-slate-600">
            En attendant, vos adhérents peuvent toujours payer par <span className="font-semibold">PayPal</span>.
          </p>
        </div>

        <div className="step-enter step-enter-4 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onResume}
            disabled={connecting}
            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
            style={{ backgroundColor: "#635BFF" }}
          >
            {connecting ? (
              <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Redirection…</>
            ) : (
              <>Reprendre sur Stripe →</>
            )}
          </button>
          <button
            onClick={onDashboard}
            className="flex-1 sm:flex-none px-5 py-3.5 rounded-xl text-sm font-medium text-slate-500 border border-slate-200 hover:bg-slate-50 transition-all"
          >
            Retour au tableau de bord
          </button>
        </div>

        <p className="mt-4 text-xs text-slate-400">
          Besoin d'aide ?{" "}
          <button className="underline underline-offset-2" style={{ color: "#059669" }}>
            Contacter le support Hafani MS
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="step-enter step-enter-1">
      <div className="rounded-2xl overflow-hidden mb-6" style={{ background: "linear-gradient(135deg, #111827 0%, #1F2937 100%)" }}>
        <div className="p-6 sm:p-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(255,255,255,0.1)" }}>
            <span className="text-2xl">✅</span>
          </div>
          <div>
            <h2 className="text-2xl text-white mb-1.5"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
              Merci ! Vos informations sont en cours de vérification par Stripe.
            </h2>
            <p className="text-sm" style={{ color: "#9CA3AF" }}>
              Délai habituel : quelques minutes à 24 h. Vous recevrez un email dès que vos paiements seront activés.
            </p>
          </div>
        </div>
      </div>

      <div className="step-enter step-enter-2 bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5 mb-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">État de l'activation</p>
        <StatusRow label="Identité de l'association" state="checking" />
        <StatusRow label="Représentant légal" state="checking" />
        <StatusRow label="Coordonnées bancaires" state="checking" />
        <StatusRow label="Paiements par carte" state="pending" />
        <StatusRow label="Prélèvement SEPA" state="pending" />
      </div>

      <div className="step-enter step-enter-3 flex items-start gap-3 rounded-xl px-4 py-3 mb-6"
           style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }}>
        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#059669" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-slate-600">
          En attendant, vos adhérents peuvent toujours payer par <span className="font-semibold">PayPal</span>.
        </p>
      </div>

      <div className="step-enter step-enter-4 flex flex-col sm:flex-row gap-3">
        <button
          onClick={onDashboard}
          className="flex-1 sm:flex-none flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
          style={{ backgroundColor: "#111827" }}
        >
          Retour au tableau de bord
        </button>
      </div>
    </div>
  );
};

/* ─── Main page component ─── */
export const StripeOnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState("checklist");
  const [association, setAssociation] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const adminEmail = localStorage.getItem("hafani_admin_email") || "";

  const fetchAssociation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/associations/me`, { headers: authHeaders() });
      setAssociation(res.data);
    } catch {
      // ignore
    }
  }, []);

  const fetchStripeStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/associations/me/stripe-status`, { headers: authHeaders() });
      setStripeStatus(res.data);
    } catch {
      toast.error("Impossible de récupérer l'état Stripe.");
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchAssociation();
    if (searchParams.get("step") === "return") {
      setStep("return");
      fetchStripeStatus();
    }
  }, [fetchAssociation, fetchStripeStatus, searchParams]);

  const handleConnectToStripe = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/associations/me/stripe-onboarding`, {}, { headers: authHeaders() });
      window.location.href = res.data.url;
    } catch {
      toast.error("Erreur lors de l'initialisation Stripe.");
      setConnecting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{css}</style>

      {/* Top nav */}
      <nav style={{ backgroundColor: "#111827" }}>
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => step === "prefill" ? setStep("checklist") : navigate("/app/dashboard")}
            className="flex items-center gap-2 text-sm transition-colors"
            style={{ color: "#6B7280" }}
            onMouseEnter={e => e.currentTarget.style.color = "#D1D5DB"}
            onMouseLeave={e => e.currentTarget.style.color = "#6B7280"}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {step === "prefill" ? "Retour" : "Retour au tableau de bord"}
          </button>
          <span
            className="text-sm font-semibold tracking-wide"
            style={{ color: "#059669", fontFamily: "'DM Sans', sans-serif" }}
          >
            Hafani MS
          </span>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-10">
        {step !== "return" && <StepIndicator current={step} />}

        {step === "checklist" && (
          <ChecklistStep
            onContinue={() => setStep("prefill")}
            onSkip={() => navigate("/app/dashboard")}
          />
        )}

        {step === "prefill" && (
          <PrefillStep
            association={association}
            adminEmail={adminEmail}
            onConfirm={handleConnectToStripe}
            onBack={() => setStep("checklist")}
            connecting={connecting}
          />
        )}

        {step === "return" && (
          <>
            <StepIndicator current="return" />
            <ReturnStep
              stripeStatus={stripeStatus}
              loading={loadingStatus}
              onResume={handleConnectToStripe}
              onDashboard={() => navigate("/app/dashboard")}
              connecting={connecting}
            />
          </>
        )}
      </div>
    </div>
  );
};
